/* Pulse by ALTO — Sprint 1 + 2
   Cloudflare Worker streaming Claude Opus 4.7 over SSE.
   Set window.PULSE_API_URL to the deployed worker URL (e.g. https://pulse-worker.xxx.workers.dev).
   If unset → falls back to local mock answers (offline / preview / no-backend mode).
*/

(function () {
  'use strict';

  /* ── Backend config ─────────────────────────────────────── */
  var PULSE_API_URL = (window.PULSE_API_URL || '').replace(/\/$/, '');
  var USE_BACKEND   = PULSE_API_URL.length > 0;

  /* ── Orb controller (CSS state machine) ─────────────────── */
  function Orb(el, ringsEl) {
    this.el = el;
    this.ringsEl = ringsEl || null;
    this.state = 'idle';
    this.audioAmp = 0;
    this.targetAmp = 0;
    this.raf = null;
    this.start();
  }

  Orb.prototype.start = function () {
    var self = this;
    function loop() {
      self.audioAmp += (self.targetAmp - self.audioAmp) * 0.18;
      self.el.style.setProperty('--alto-orb-amp', self.audioAmp.toFixed(3));
      self.raf = requestAnimationFrame(loop);
    }
    loop();
  };

  Orb.prototype.setState = function (name) {
    if (name === this.state) return;
    this.el.classList.remove('is-idle', 'is-listening', 'is-thinking', 'is-speaking');
    this.el.classList.add('is-' + name);
    /* Toggle the Siri-style emanating rings only when Pulse is speaking */
    if (this.ringsEl) {
      this.ringsEl.classList.toggle('is-active', name === 'speaking');
    }
    this.state = name;
  };

  Orb.prototype.setAudioAmp = function (v) {
    this.targetAmp = Math.max(0, Math.min(1, v));
  };

  Orb.prototype.destroy = function () {
    cancelAnimationFrame(this.raf);
  };

  /* ── Mock chat answers (Sprint 1 backend not yet hooked) ── */
  var MOCK = {
    prix: "Trois packs. **Starter 2 800€ HT** (site one-page + identité légère). **Signature 4 500€ HT** (recommandé, multi-pages premium + booking + plan stratégique 12 mois + Pulse offert). **Premium 7 200€ HT** (Signature + accompagnement 3 mois). Tu veux qu'on regarde ce qui colle à ton projet ? [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    cher: "Calcul direct. Pack Signature 4 500€. Une marque e-commerce moyenne avec 50 000 visites par mois et 1,5% de conversion, ça fait 75 ventes à 80€ de panier moyen. Après refonte neuro, on passe à 2,5% de conversion, soit 125 ventes. Gain : 4 000€ par mois récurrent. Amorti en 5 semaines. Source : moyenne ALTO sur mes 4 derniers clients refondus. On calcule sur tes chiffres ? [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    webflow: "L'outil compte moins que la neuroscience derrière chaque bloc. Webflow est un outil. La conversion vient de la hiérarchie visuelle, du copy, du parcours mental. Lindgaard 2006 (Carleton) : 50 millisecondes pour juger un site. On regarde ce qui bloque sur le tien ? [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    wordpress: "Non. Shopify pour les marques qui vendent du produit, Webflow custom ou code natif pour les marques de service. Wordpress ralentit les Core Web Vitals et freine la conversion mobile. Étude Google : 0,1 seconde de chargement supplémentaire, c'est 8,4% de conversion en moins. On audit ton site actuel ? [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    difference: "La différence, c'est que chaque bloc de ton site est mappé sur un mécanisme cérébral identifié. On ne fait pas du design qui plaît, on fait du design qui convertit. Cas concret : My Essencil. On a refait la fiche produit en repositionnant la preuve sociale au-dessus du prix. Conversion multipliée par 1,7 en 6 semaines. Tu veux voir ce que ça donnerait sur ton site ? [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    audit: "Audit gratuit 30 min. On regarde ton site, ton offre, ce qui bloque. Si on peut t'aider, on te propose un cadre clair. Si on n'est pas les bonnes personnes, on te le dit. [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    contact: "Direct WhatsApp : [+33 6 67 20 22 31](https://wa.me/33667202231). Ou Calendly 30 min : [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)",
    default: "Bonne question. Je note. Pour une réponse précise sur ton cas, parlons-en 30 min : [calendly.com/byaltostudiocontact/30min](https://calendly.com/byaltostudiocontact/30min)"
  };

  function pickAnswer(text) {
    var t = text.toLowerCase();
    if (/prix|tarif|combien|co[uû]te|pack/.test(t)) return MOCK.prix;
    if (/cher|budget|trop|onereux/.test(t))         return MOCK.cher;
    if (/webflow|squarespace|wix/.test(t))           return MOCK.webflow;
    if (/wordpress|wp/.test(t))                      return MOCK.wordpress;
    if (/diff[eé]rence|pourquoi|agence|shopify/.test(t)) return MOCK.difference;
    if (/audit|gratuit|essai|test/.test(t))          return MOCK.audit;
    if (/contact|call|appel|whatsapp|tel/.test(t))   return MOCK.contact;
    return MOCK.default;
  }

  /* Minimal markdown : **bold**, [text](url), + auto-button for Calendly */
  var CALENDLY_URL = 'https://calendly.com/byaltostudiocontact/30min';
  var CALENDLY_BTN = '<a class="alto-comp-cta-btn" href="' + CALENDLY_URL + '" target="_blank" rel="noopener">'
    + 'Prends rendez-vous avec Louis-Gabriel'
    + '<span class="alto-comp-cta-arrow" aria-hidden="true">→</span>'
    + '</a>';

  /* Inline transforms : **bold** + markdown links. À appliquer dans chaque bloc. */
  function mdInline(s) {
    return s
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  }

  /* Markdown structuré : paragraphes (\n\n), listes à puces, listes numérotées,
     retours à la ligne simples (\n → <br>). Pour des réponses longues bien aérées. */
  function md(str) {
    if (!str) return '';
    var CTA_TOKEN = 'ALTOCTABTN';
    /* 1. Échappe HTML + masque les URLs Calendly avant tout split */
    var s = String(str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/\[[^\]]+\]\((?:https?:\/\/)?(?:www\.)?calendly\.com\/byaltostudiocontact\/30min\)/gi, CTA_TOKEN)
      .replace(/(?:https?:\/\/)?(?:www\.)?calendly\.com\/byaltostudiocontact\/30min/gi, CTA_TOKEN);

    /* 2. Découpe en blocs séparés par une ligne vide (paragraphes) */
    var blocks = s.split(/\n\s*\n/);

    var html = blocks.map(function (block) {
      block = block.replace(/^\s+|\s+$/g, '');
      if (!block) return '';
      var lines = block.split('\n');

      /* Liste à puces : toutes les lignes commencent par -, *, ou • */
      var isBulletList = lines.length >= 1 && lines.every(function (l) {
        return /^\s*[-*•]\s+/.test(l);
      });
      if (isBulletList) {
        return '<ul class="alto-comp-list">' + lines.map(function (l) {
          return '<li>' + mdInline(l.replace(/^\s*[-*•]\s+/, '')) + '</li>';
        }).join('') + '</ul>';
      }

      /* Liste numérotée : "1.", "2)", etc. */
      var isNumberedList = lines.length >= 1 && lines.every(function (l) {
        return /^\s*\d+[.)]\s+/.test(l);
      });
      if (isNumberedList) {
        return '<ol class="alto-comp-list">' + lines.map(function (l) {
          return '<li>' + mdInline(l.replace(/^\s*\d+[.)]\s+/, '')) + '</li>';
        }).join('') + '</ol>';
      }

      /* Paragraphe standard : single \n → <br> à l'intérieur */
      return '<p>' + mdInline(lines.join('<br>')) + '</p>';
    }).filter(Boolean).join('');

    /* 3. Substitue le token Calendly par le bouton final (aucune regex ne le re-touche) */
    return html.split(CTA_TOKEN).join(CALENDLY_BTN);
  }

  /* ── Voice input (Web Speech API) ───────────────────────── */
  function SpeechIn() {
    var SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return null;
    var r = new SR();
    r.lang = 'fr-FR';
    r.continuous = true;
    r.interimResults = true;
    r.maxAlternatives = 1;
    return r;
  }

  /* ── Mic amplitude meter (AnalyserNode) ─────────────────── */
  function MicMeter(onAmp) {
    this.onAmp = onAmp;
    this.ctx = null;
    this.stream = null;
    this.raf = null;
  }
  MicMeter.prototype.start = function () {
    var self = this;
    if (self.ctx) return Promise.resolve();
    return navigator.mediaDevices.getUserMedia({ audio: true }).then(function (stream) {
      self.stream = stream;
      var AC = window.AudioContext || window.webkitAudioContext;
      self.ctx = new AC();
      var src = self.ctx.createMediaStreamSource(stream);
      var analyser = self.ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      var buf = new Uint8Array(analyser.frequencyBinCount);
      function loop() {
        analyser.getByteFrequencyData(buf);
        var sum = 0;
        for (var i = 0; i < buf.length; i++) sum += buf[i];
        var amp = (sum / buf.length) / 128;
        self.onAmp(Math.min(1, amp));
        self.raf = requestAnimationFrame(loop);
      }
      loop();
    });
    /* let permission errors bubble up to toggleMic's caller */
  };
  MicMeter.prototype.stop = function () {
    cancelAnimationFrame(this.raf);
    if (this.stream) this.stream.getTracks().forEach(function (t) { t.stop(); });
    if (this.ctx)    try { this.ctx.close(); } catch (e) {}
    this.ctx = null; this.stream = null;
    this.onAmp(0);
  };

  /* ── Main Companion controller ──────────────────────────── */
  function Companion() {
    var self = this;
    this.dock     = document.querySelector('[data-alto-comp-open]');
    this.modal    = document.getElementById('alto-comp-modal');
    if (!this.dock || !this.modal) return;
    this.backdrop = document.querySelector('[data-alto-comp-backdrop]');
    this.closeBtn = this.modal.querySelector('[data-alto-comp-close]');
    this.form     = this.modal.querySelector('[data-alto-comp-form]');
    this.input    = this.modal.querySelector('[data-alto-comp-input]');
    this.micBtn   = this.modal.querySelector('[data-alto-comp-mic]');
    this.sendBtn  = this.modal.querySelector('[data-alto-comp-send]');
    this.msgs     = this.modal.querySelector('[data-alto-comp-messages]');
    this.status   = this.modal.querySelector('[data-alto-comp-status]');
    this.orbEl    = this.modal.querySelector('#alto-comp-orb');
    this.ringsEl  = this.modal.querySelector('[data-alto-comp-rings]');
    this.suggestionsEl = this.modal.querySelector('[data-alto-comp-suggestions]');
    this.calOverlay = this.modal.querySelector('[data-alto-comp-cal]');
    this.calCloseBtn = this.modal.querySelector('[data-alto-comp-cal-close]');
    this.bubble = document.querySelector('[data-alto-comp-bubble]');
    this.bubbleClose = document.querySelector('[data-alto-comp-bubble-close]');

    this.orb = null;
    this.opened = false;
    this.micActive = false;
    this.speech = SpeechIn();
    this.mic = new MicMeter(function (a) { if (self.orb) self.orb.setAudioAmp(a); });

    this._loadHistory();
    this._renderHistory();
    this.bind();
    /* Si l'historique est vide (1ère visite ou cleared), on schedule la bulle.
       Si user a déjà conversé, pas besoin d'invite. */
    if (!this.history || this.history.length === 0) {
      this._showBubble();
    }
  }

  /* Web Audio API unlock — works reliably on iOS Safari, Chrome mobile, and desktop.
     Called on every user gesture (clicks, keypresses) — idempotent after first success.
     We use AudioBufferSourceNode for all TTS playback (not HTMLAudio) because once an
     AudioContext is created+resumed inside a gesture, it stays unlocked for the page
     session and source nodes can be played at any time, even async after fetch. */
  Companion.prototype._unlockAudio = function () {
    var self = this;
    try {
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!this._audioCtx) {
        this._audioCtx = new AC();
      }
      if (this._audioCtx.state === 'suspended') {
        var p = this._audioCtx.resume();
        if (p && p.catch) p.catch(function (err) { console.warn('[Pulse] ctx.resume() failed :', err); });
      }
      /* Play a 1-sample silent buffer to fully unlock playback */
      if (!this._audioUnlocked) {
        var buf = this._audioCtx.createBuffer(1, 1, 22050);
        var src = this._audioCtx.createBufferSource();
        src.buffer = buf;
        src.connect(this._audioCtx.destination);
        src.start(0);
        this._audioUnlocked = true;
      }
    } catch (e) { console.warn('[Pulse] _unlockAudio threw :', e); }
  };

  /* Bubble "Hey, je suis Pulse" : apparait 4s après load, persiste jusqu'au clic,
     sessionStorage évite la ré-apparition une fois fermée. */
  var BUBBLE_KEY = 'alto-pulse-bubble-dismissed';
  Companion.prototype._showBubble = function () {
    if (!this.bubble) return;
    try { if (sessionStorage.getItem(BUBBLE_KEY) === '1') return; } catch (e) {}
    var self = this;
    /* Délai 4s + check : ne pas afficher si modal déjà ouvert ou splash actif */
    setTimeout(function () {
      if (self.opened) return;
      if (document.body.classList.contains('alto-splash-active')) {
        /* Splash encore actif, on attend qu'il se ferme */
        var iv = setInterval(function () {
          if (!document.body.classList.contains('alto-splash-active')) {
            clearInterval(iv);
            if (!self.opened) self._actuallyShowBubble();
          }
        }, 500);
        return;
      }
      self._actuallyShowBubble();
    }, 4000);
  };
  Companion.prototype._actuallyShowBubble = function () {
    if (!this.bubble) return;
    this.bubble.hidden = false;
    /* reflow pour que la transition fire */
    void this.bubble.offsetWidth;
    this.bubble.classList.add('is-visible');
  };
  Companion.prototype._dismissBubble = function (markPermanent) {
    if (!this.bubble) return;
    this.bubble.classList.remove('is-visible');
    var self = this;
    setTimeout(function () { if (self.bubble) self.bubble.hidden = true; }, 400);
    if (markPermanent) {
      try { sessionStorage.setItem(BUBBLE_KEY, '1'); } catch (e) {}
    }
  };

  Companion.prototype.bind = function () {
    var self = this;
    this.dock.addEventListener('click', function () { self._unlockAudio(); self.open(); });
    /* Bubble : clic ouvre Pulse, clic sur ✕ dismiss permanent */
    if (this.bubble) {
      this.bubble.addEventListener('click', function (e) {
        if (e.target.closest('[data-alto-comp-bubble-close]')) return; /* géré ci-dessous */
        self._dismissBubble(true);
        self._unlockAudio();
        self.open();
      });
    }
    if (this.bubbleClose) {
      this.bubbleClose.addEventListener('click', function (e) {
        e.stopPropagation();
        self._dismissBubble(true);
      });
    }
    this.closeBtn.addEventListener('click', function () { self.close(); });
    if (this.backdrop) this.backdrop.addEventListener('click', function () { self.close(); });
    document.addEventListener('keydown', function (e) {
      if (!self.opened) return;
      if (e.key === 'Escape') {
        /* Si le calendrier est ouvert, on le ferme en priorité */
        if (self.calOverlay && self.calOverlay.classList.contains('is-open')) {
          self._closeCalendly();
        } else {
          self.close();
        }
      }
    });
    this.input.addEventListener('input', function () {
      self.sendBtn.classList.toggle('is-visible', self.input.value.trim().length > 0);
    });
    this.form.addEventListener('submit', function (e) {
      e.preventDefault();
      self._unlockAudio();
      var t = self.input.value.trim();
      if (!t) return;
      self.send(t);
      self.input.value = '';
      self.sendBtn.classList.remove('is-visible');
    });
    this.micBtn.addEventListener('click', function () { self._unlockAudio(); self.toggleMic(); });
    /* Suggested question chips */
    if (this.suggestionsEl) {
      this.suggestionsEl.addEventListener('click', function (e) {
        var chip = e.target.closest('[data-alto-comp-chip]');
        if (!chip) return;
        self._unlockAudio();
        self.send(chip.textContent.trim());
        self._hideSuggestions();
      });
    }
    /* Calendly CTA click : open the INLINE liquid-glass overlay (no redirect).
       Also fire warm hand-off in background. */
    this.msgs.addEventListener('click', function (e) {
      var cta = e.target.closest('.alto-comp-cta-btn');
      if (!cta) return;
      e.preventDefault(); /* don't navigate away */
      self._sendHandoff('calendly_click');
      self._openCalendly();
    });
    if (this.calCloseBtn) {
      this.calCloseBtn.addEventListener('click', function () { self._closeCalendly(); });
    }
    /* Orb click = same as mic button (Siri-style tap-to-speak) */
    if (this.orbEl) {
      this.orbEl.addEventListener('click', function () { self._unlockAudio(); self.toggleMic(); });
      this.orbEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self._unlockAudio();
          self.toggleMic();
        }
      });
    }
  };

  /* normalizeScroll(true) entre en contention avec le scrub du pin cinematic
     → sursauts visibles sur iOS. On le désactive et on ne le restaure JAMAIS.
     iOS gère son scroll nativement → momentum natif + scrub ScrollTrigger
     cohabitent proprement. */
  Companion.prototype._disableNormalizeScroll = function () {
    if (window.ScrollTrigger && typeof window.ScrollTrigger.normalizeScroll === 'function') {
      try { window.ScrollTrigger.normalizeScroll(false); } catch (e) {}
    }
  };
  Companion.prototype._restoreNormalizeScroll = function () {
    /* no-op : on ne réactive jamais normalizeScroll(true) */
  };

  Companion.prototype.open = function () {
    var self = this;
    this._disableNormalizeScroll();
    /* Dismiss la bulle dès qu'on ouvre Pulse (par n'importe quel chemin) */
    if (this.bubble && this.bubble.classList.contains('is-visible')) {
      this._dismissBubble(true);
    }
    this.opened = true;
    this.modal.show();
    document.body.classList.add('alto-comp-locked');
    if (this.backdrop) this.backdrop.classList.add('is-open');
    requestAnimationFrame(function () {
      self.modal.classList.add('is-open');
      self.dock.classList.add('is-hidden');
      if (!self.orb) self.orb = new Orb(self.orbEl, self.ringsEl);
    });
    setTimeout(function () { self.input.focus(); }, 600);
  };

  Companion.prototype.close = function () {
    var self = this;
    if (this.micActive) this.toggleMic();
    this.opened = false;
    this.modal.classList.remove('is-open');
    if (this.backdrop) this.backdrop.classList.remove('is-open');
    this.dock.classList.remove('is-hidden');
    document.body.classList.remove('alto-comp-locked');
    setTimeout(function () { self.modal.close(); }, 550);
    this._restoreNormalizeScroll();
  };

  /* True if user is already pinned to (or near) the bottom of the messages list.
     Used to decide whether to auto-scroll on new content. If user has scrolled
     up manually to re-read, we DON'T force them back to bottom. */
  Companion.prototype._isNearBottom = function () {
    if (!this.msgs) return true;
    var threshold = 60; /* px of slack — within 60px counts as "at bottom" */
    return (this.msgs.scrollTop + this.msgs.clientHeight) >= (this.msgs.scrollHeight - threshold);
  };

  Companion.prototype.addMsg = function (role, html) {
    var wasNearBottom = this._isNearBottom();
    var el = document.createElement('div');
    el.className = 'alto-comp-msg alto-comp-msg--' + role;
    el.innerHTML = html;
    /* Copy-to-clipboard button on bot messages (icon, hover to reveal) */
    if (role === 'bot') {
      var copyBtn = document.createElement('button');
      copyBtn.className = 'alto-comp-copy';
      copyBtn.type = 'button';
      copyBtn.setAttribute('aria-label', 'Copier la réponse');
      copyBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2.5"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>';
      copyBtn.addEventListener('click', function (ev) {
        ev.stopPropagation();
        var text = el.innerText.replace(/^Copier la réponse\s*/, '').replace(/Prends rendez-vous avec Louis-Gabriel\s*→?\s*/, 'calendly.com/byaltostudiocontact/30min').trim();
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(function () {
            copyBtn.classList.add('is-copied');
            setTimeout(function () { copyBtn.classList.remove('is-copied'); }, 1400);
          }).catch(function () {});
        }
      });
      el.appendChild(copyBtn);
    }
    this.msgs.appendChild(el);
    /* Only auto-scroll if user was already near the bottom. If they scrolled up
       to re-read, don't yank them back down. */
    if (wasNearBottom) this.msgs.scrollTop = this.msgs.scrollHeight;
    return el;
  };

  Companion.prototype.setStatus = function (text) {
    if (this.status) this.status.textContent = text;
  };

  Companion.prototype._hideSuggestions = function () {
    if (this.suggestionsEl) this.suggestionsEl.classList.add('is-hidden');
  };

  /* Open the inline Calendly overlay (liquid glass) instead of redirecting away.
     iframe is lazy-injected on first open to avoid loading Calendly on every page load. */
  Companion.prototype._openCalendly = function () {
    if (!this.calOverlay) {
      /* Fallback : if overlay markup missing, redirect classically */
      window.open('https://calendly.com/byaltostudiocontact/30min', '_blank', 'noopener');
      return;
    }
    var frame = this.calOverlay.querySelector('.alto-comp-cal-frame');
    if (frame && !frame.querySelector('iframe')) {
      var iframe = document.createElement('iframe');
      /* embed_domain + embed_type=Inline force la version intégrable de Calendly */
      iframe.src = 'https://calendly.com/byaltostudiocontact/30min?hide_event_type_details=0&hide_gdpr_banner=1&embed_type=Inline&embed_domain=' + encodeURIComponent(location.hostname);
      iframe.title = 'Réserver un rendez-vous avec Louis-Gabriel';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'clipboard-write');
      frame.appendChild(iframe);
    }
    this.calOverlay.hidden = false;
    /* Force reflow so the transition fires */
    void this.calOverlay.offsetWidth;
    this.calOverlay.classList.add('is-open');
  };

  Companion.prototype._closeCalendly = function () {
    if (!this.calOverlay) return;
    this.calOverlay.classList.remove('is-open');
    var self = this;
    setTimeout(function () {
      if (!self.calOverlay.classList.contains('is-open')) self.calOverlay.hidden = true;
    }, 400);
  };

  /* Fire-and-forget hand-off : POST conversation transcript to worker so that
     Louis-Gabriel arrives in his Calendly call with full context. Failures are silent. */
  Companion.prototype._sendHandoff = function (trigger) {
    if (!USE_BACKEND) return;
    if (!this.history || this.history.length === 0) return;
    /* Don't double-fire if user clicks Calendly multiple times in same session */
    if (this._handoffSent) return;
    this._handoffSent = true;
    try {
      var payload = {
        trigger: trigger || 'manual',
        history: this.history,
        meta: {
          user_agent: navigator.userAgent || '',
          page_url: location.href || '',
          timestamp: new Date().toISOString()
        }
      };
      /* Use keepalive so the request survives even if the user navigates away to Calendly */
      fetch(PULSE_API_URL + '/api/handoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  };

  /* History kept on the client (backend is stateless), persisted 24h in sessionStorage */
  var HISTORY_KEY = 'alto-pulse-history-v1';
  var HISTORY_TTL_MS = 24 * 60 * 60 * 1000;

  Companion.prototype._loadHistory = function () {
    this.history = [];
    try {
      var raw = window.sessionStorage && sessionStorage.getItem(HISTORY_KEY);
      if (!raw) return;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || !Array.isArray(parsed.messages)) return;
      if (Date.now() - parsed.ts > HISTORY_TTL_MS) {
        sessionStorage.removeItem(HISTORY_KEY);
        return;
      }
      this.history = parsed.messages.slice();
    } catch (e) { this.history = []; }
  };

  Companion.prototype._saveHistory = function () {
    try {
      if (!window.sessionStorage) return;
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify({
        ts: Date.now(),
        messages: this.history.slice(-20) /* same cap as backend */
      }));
    } catch (e) {}
  };

  Companion.prototype._renderHistory = function () {
    if (!this.history || this.history.length === 0) return;
    for (var i = 0; i < this.history.length; i++) {
      var msg = this.history[i];
      var role = msg.role === 'assistant' ? 'bot' : 'user';
      this.addMsg(role, md(msg.content));
    }
    this._hideSuggestions();
    if (this.modal) this.modal.classList.add('is-conversing');
  };

  Companion.prototype.send = function (text) {
    var self = this;
    this._hideSuggestions();
    /* Marque le modal en "is-conversing" pour shrink l'orbe et donner toute la
       place aux messages (CSS mobile). */
    if (this.modal) this.modal.classList.add('is-conversing');
    this.addMsg('user', md(text));
    this.history.push({ role: 'user', content: text });
    this._saveHistory();
    if (this.orb) this.orb.setState('thinking');
    this.setStatus('Pulse réfléchit…');

    if (USE_BACKEND) {
      this.streamFromBackend();
    } else {
      this.replyFromMock(text);
    }
  };

  /* ── Backend streaming via SSE ──────────────────────────── */
  Companion.prototype.streamFromBackend = function () {
    var self = this;
    /* Add a bot bubble with animated 3-dot typing indicator. First token will replace it. */
    var botEl = this.addMsg('bot', '<span class="alto-comp-typing" aria-hidden="true"><span></span><span></span><span></span></span>');
    botEl.classList.add('is-thinking');
    var rawText = '';
    var flushedTo = 0; /* index in rawText up to which we've already enqueued TTS */
    var firstChunkArrived = false;
    var finished = false; /* guard against double-finish */
    var MIN_FLUSH_LEN = 20; /* don't fire TTS on chunks shorter than this */

    /* Stop any audio that's still playing from a previous turn + reset TTS queue */
    self.stopCurrentAudio();
    self._streamDone = false;

    /* Mask dots that should NOT be treated as sentence ends.
       Replaces '.' with '' (same char count → indexes preserved against rawText). */
    function maskNonSentenceDots(text) {
      return text
        /* URLs / domains : byaltostudio.com, calendly.com/x, https://... */
        .replace(/\b[\w-]+(?:\.[\w-]+)+(?:\/[^\s]*)?/g, function (m) { return m.replace(/\./g, ''); })
        /* Common French abbreviations: M., Mme., Mlle., Dr., etc., cf., p.ex., c.-à-d. */
        .replace(/\b(?:[A-ZÀ-Ý][a-zà-ÿ]?|etc|cf|p\.ex|c\.-à-d|env|env|réf|tél|ex|p)\./gi, function (m) { return m.replace(/\./g, ''); })
        /* Markdown numbered lists at start of line or after whitespace: "1.", "2.", "10." */
        .replace(/(^|\s)(\d+)\./g, function (m, pre, n) { return pre + n + ''; });
    }

    function flushSentences(force) {
      /* Find sentence boundaries in rawText after flushedTo, enqueue to TTS.
         If a sentence boundary is reached but the chunk is too short (< MIN_FLUSH_LEN),
         we keep scanning for the NEXT boundary to combine short sentences with the
         following one. This avoids the burst-at-end-of-stream problem where short
         leading sentences delay all flushes until force=true, then fire all fetches
         at once and hit ElevenLabs concurrency limits. */
      var SENT_END = /[.!?…]+[\s\n]+/g;
      while (true) {
        var pending = rawText.slice(flushedTo);
        var maskedPending = maskNonSentenceDots(pending);
        SENT_END.lastIndex = 0;
        var m;
        var chosenEnd = -1;
        while ((m = SENT_END.exec(maskedPending)) !== null) {
          var candidate = m.index + m[0].length;
          var candidateLen = pending.slice(0, candidate).trim().length;
          if (force || candidateLen >= MIN_FLUSH_LEN) {
            chosenEnd = candidate;
            break;
          }
          /* sentence too short, scan for next boundary to combine */
        }
        if (chosenEnd === -1) break;
        var sentence = pending.slice(0, chosenEnd).trim();
        flushedTo += chosenEnd;
        if (sentence) self.speakSentence(sentence);
      }
      if (force) {
        var tail = rawText.slice(flushedTo).trim();
        if (tail) { flushedTo = rawText.length; self.speakSentence(tail); }
      }
    }

    fetch(PULSE_API_URL + '/api/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: this.history })
    }).then(function (resp) {
      if (!resp.ok || !resp.body) {
        throw new Error('HTTP ' + resp.status);
      }
      var reader = resp.body.getReader();
      var decoder = new TextDecoder('utf-8');
      var buffer = '';

      function pump() {
        return reader.read().then(function (chunk) {
          if (chunk.done) { finish(); return; }
          buffer += decoder.decode(chunk.value, { stream: true });

          /* SSE frames separated by \n\n */
          var parts = buffer.split('\n\n');
          buffer = parts.pop() || '';
          parts.forEach(function (frame) {
            var line = frame.trim();
            if (!line.startsWith('data:')) return;
            var json = line.slice(5).trim();
            if (!json) return;
            var evt;
            try { evt = JSON.parse(json); } catch (e) { return; }

            if (evt.type === 'text' && typeof evt.delta === 'string') {
              if (!firstChunkArrived) {
                firstChunkArrived = true;
                botEl.classList.remove('is-thinking'); /* swap dots for real text */
                if (self.orb) self.orb.setState('speaking');
                self.setStatus('Pulse parle…');
              }
              /* Capture scroll position BEFORE re-rendering, so we know if user
                 scrolled up to re-read. If they did, we don't force them back down. */
              var streamWasNearBottom = self._isNearBottom();
              rawText += evt.delta;
              botEl.innerHTML = md(rawText);
              if (streamWasNearBottom) self.msgs.scrollTop = self.msgs.scrollHeight;
              if (self.orb) self.orb.setAudioAmp(0.3 + Math.random() * 0.5);
              flushSentences(false); /* fire TTS as soon as sentence completes */
            } else if (evt.type === 'done') {
              finish();
            } else if (evt.type === 'error') {
              fallback('Backend error : ' + (evt.error || 'unknown'));
            }
          });
          return pump();
        });
      }

      function finish() {
        if (finished) return; /* prevent double-call from done event + stream close */
        finished = true;
        self._streamDone = true;
        if (rawText.trim().length > 0) {
          self.history.push({ role: 'assistant', content: rawText });
          self._saveHistory();
          /* Flush any remaining text as final TTS chunk */
          flushSentences(true);
          /* If nothing got queued (no backend voice etc.), reset orb */
          if (!self._sentencePlaying && (!self._sentenceQueue || self._sentenceQueue.length === 0)) {
            if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
            self.setStatus('Touchez pour parler ou tapez votre question');
          }
        } else {
          if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
          self.setStatus('Touchez pour parler ou tapez votre question');
        }
      }

      function fallback(msg) {
        if (finished) return;
        finished = true;
        if (rawText.length === 0) {
          botEl.innerHTML = md(pickAnswer(self.history[self.history.length - 1].content));
        }
        if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
        self.setStatus('Touchez pour parler ou tapez votre question');
        console.warn('[Pulse] ' + msg);
      }

      return pump();
    }).catch(function (err) {
      if (finished) return;
      finished = true;
      /* Network failure → fall back to mock so user always gets a reply */
      if (rawText.length === 0) {
        botEl.innerHTML = md(pickAnswer(self.history[self.history.length - 1].content));
      }
      if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
      self.setStatus('Touchez pour parler ou tapez votre question');
      console.warn('[Pulse] fetch failed, mock used :', err);
    });
  };

  /* Stop any audio currently playing (defensive — prevents overlap) */
  Companion.prototype.stopCurrentAudio = function () {
    /* Clear the sentence queue so no pending TTS request continues */
    this._sentenceQueue = [];
    this._sentencePlaying = false;
    /* Bump turnId so any in-flight fetch resolves stale and bails */
    this._speakTurnId = (this._speakTurnId || 0) + 1;
    this._releaseCurrentAudioRefs();
  };

  /* Internal : release the source node + analyser RAF for the current TTS phrase.
     We DO NOT close the AudioContext (this._audioCtx) — it stays alive for the page session
     so subsequent phrases play without needing a new user gesture. */
  Companion.prototype._releaseCurrentAudioRefs = function () {
    if (this._currentSource) {
      try { this._currentSource.onended = null; } catch (e) {}
      try { this._currentSource.stop(0); } catch (e) {}
      try { this._currentSource.disconnect(); } catch (e) {}
      this._currentSource = null;
    }
    if (this._currentAnalyser) {
      try { this._currentAnalyser.disconnect(); } catch (e) {}
      this._currentAnalyser = null;
    }
    if (this._currentGain) {
      try { this._currentGain.disconnect(); } catch (e) {}
      this._currentGain = null;
    }
    if (this._currentAudioRaf) {
      cancelAnimationFrame(this._currentAudioRaf);
      this._currentAudioRaf = null;
    }
  };

  /* ── Voice output (ElevenLabs via worker /api/voice) ────── */

  /* Strip markdown / URLs / phone numbers so TTS doesn't read them literally.
     Also expand abbreviations that ElevenLabs reads phonetically wrong in French. */
  /* Mots/acronymes qu'on garde EN MAJUSCULES côté TTS (noms propres, marques) */
  var TTS_KEEP_UPPER = ['ALTO', 'PULSE', 'NEURO', 'HALO', 'FLOW', 'CRM', 'SEO', 'PDF', 'IA', 'AI', 'API', 'UI', 'UX', 'SAS', 'SARL', 'TVA', 'WhatsApp', 'PME'];
  var TTS_KEEP_UPPER_SET = (function () { var s = {}; for (var i = 0; i < TTS_KEEP_UPPER.length; i++) s[TTS_KEEP_UPPER[i]] = true; return s; })();

  function sanitizeForSpeech(text) {
    return text
      .replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, '$1')      /* [label](url) → label */
      .replace(/\*\*([^*]+)\*\*/g, '$1')                       /* **bold** → bold */
      .replace(/`([^`]+)`/g, '$1')
      .replace(/calendly\.com\/byaltostudiocontact\/30min/g, 'le lien Calendly dans le chat')
      .replace(/byaltostudio\.com/g, 'le site')
      .replace(/\+33 6 67 20 22 31/g, 'le WhatsApp en bas du message')
      /* "30 min" / "30min" / "30 mn" → "30 minutes" */
      .replace(/\b(\d+)\s*(?:min|mn)\b\.?/gi, '$1 minutes')
      /* "3×" / "2×" → "3 fois" */
      .replace(/(\d+)\s*×/g, '$1 fois')
      /* "1h30" → "une heure trente", "2h" → "deux heures" */
      .replace(/\b1h(\d{2})\b/g, '1 heure $1')
      .replace(/\b(\d+)h(\d{2})\b/g, '$1 heures $2')
      .replace(/\b(\d+)h\b/g, '$1 heures')
      /* CRITIQUE : ElevenLabs lit les ALL-CAPS comme de l'anglais ("LIT" → "Lite",
         "TON" → "Tone", "VIT" → "Vite"). On lowercase tout mot ALL-CAPS (≥ 2 chars)
         qui n'est pas dans l'allowlist de noms propres. Le chat reste en majuscules. */
      .replace(/\b([A-ZÀ-Ý]{2,})\b/g, function (m) {
        if (TTS_KEEP_UPPER_SET[m]) return m; /* garde NEURO/HALO/FLOW/ALTO/etc. */
        return m.toLowerCase();
      })
      /* Phonétique française des verbes courts au présent (sinon TTS dit "lite", "vite", "tone") */
      .replace(/\blit\b/g, 'li')
      .replace(/\bvit\b/g, 'vi');
  }

  /* Public single-shot speak (used by mock fallback) */
  Companion.prototype.speak = function (text) {
    if (!USE_BACKEND) { this.fakeSpeak(text); return; }
    this.stopCurrentAudio();
    this.speakSentence(text);
  };

  /* Simple promise semaphore : caps concurrent ElevenLabs fetches.
     Starter tier allows ~3 concurrent generations, so we stay at 2 to be safe
     across tiers and avoid silent 429 drops at end-of-stream flush bursts. */
  function Semaphore(max) { this.max = max; this.current = 0; this.waiting = []; }
  Semaphore.prototype.acquire = function () {
    var self = this;
    return new Promise(function (resolve) {
      if (self.current < self.max) { self.current++; resolve(); }
      else { self.waiting.push(resolve); }
    });
  };
  Semaphore.prototype.release = function () {
    if (this.waiting.length > 0) this.waiting.shift()();
    else this.current--;
  };

  /* Kick off the network fetch + decode for one sentence immediately.
     Returns a promise of the decoded AudioBuffer (or null if decoding fails).
     Throttled by a 2-permit semaphore to respect ElevenLabs concurrency limits. */
  Companion.prototype._fetchAndDecode = function (spokenText) {
    var self = this;
    if (!self._fetchSemaphore) self._fetchSemaphore = new Semaphore(2);

    return self._fetchSemaphore.acquire().then(function () {
      return fetch(PULSE_API_URL + '/api/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: spokenText })
      }).then(function (resp) {
        self._fetchSemaphore.release();
        if (!resp.ok) {
          var status = resp.status;
          if (status === 402) self.setStatus('Voix indisponible (quota ElevenLabs dépassé)');
          else if (status === 429) self.setStatus('Voix temporairement saturée, réessayez dans quelques secondes');
          throw new Error('Voice HTTP ' + status);
        }
        return resp.arrayBuffer();
      }, function (err) {
        /* Network error : still release the permit so subsequent fetches can proceed */
        self._fetchSemaphore.release();
        throw err;
      });
    }).then(function (arrayBuffer) {
      /* Ensure an AudioContext is available for decoding */
      if (!self._audioCtx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (AC) { try { self._audioCtx = new AC(); } catch (e) {} }
      }
      if (!self._audioCtx) throw new Error('No AudioContext');
      return new Promise(function (resolve, reject) {
        try {
          var ret = self._audioCtx.decodeAudioData(arrayBuffer, resolve, reject);
          if (ret && ret.then) ret.then(resolve, reject);
        } catch (e) { reject(e); }
      });
    });
  };

  /* Enqueue a sentence to TTS. Fetch + decode start IMMEDIATELY in parallel —
     so by the time the queue gets to it, the audio is (usually) ready in memory.
     This eliminates the gap between consecutive sentences. */
  Companion.prototype.speakSentence = function (text) {
    var self = this;
    if (!USE_BACKEND || !text || !text.trim()) return;
    var spoken = sanitizeForSpeech(text).trim();
    if (!spoken) return;
    if (!self._sentenceQueue) self._sentenceQueue = [];
    /* Fetch+decode with 1 automatic retry on any failure (network blip, 429, decode err).
       Each retry waits 350ms before retrying, giving ElevenLabs time to recover. */
    var bufferPromise = self._fetchAndDecode(spoken).catch(function (err) {
      console.warn('[Pulse] fetch/decode failed (retrying) for "' + spoken.slice(0, 50) + '" :', err);
      return new Promise(function (resolve) {
        setTimeout(function () {
          self._fetchAndDecode(spoken).then(resolve, function (err2) {
            console.warn('[Pulse] retry also FAILED for "' + spoken.slice(0, 50) + '" :', err2);
            resolve(null);
          });
        }, 350);
      });
    });
    self._sentenceQueue.push({ text: spoken, bufferPromise: bufferPromise });
    if (!self._sentencePlaying) self._playNextSentence();
  };

  Companion.prototype._playNextSentence = function () {
    var self = this;
    if (!self._sentenceQueue || self._sentenceQueue.length === 0) {
      self._sentencePlaying = false;
      /* Queue drained, if the chat stream is also done, reset orb */
      if (self._streamDone) {
        if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
        self.setStatus('Touchez pour parler ou tapez votre question');
      }
      return;
    }
    self._sentencePlaying = true;
    var item = self._sentenceQueue.shift();
    if (self.orb) self.orb.setState('speaking');
    self.setStatus('Pulse parle…');
    self._playFromBufferPromise(item.bufferPromise, function () { self._playNextSentence(); });
  };

  /* Play a pre-fetched + pre-decoded AudioBuffer (or wait for it if still in flight).
     Sentences are prefetched in parallel at enqueue time, so by the time we get here
     the buffer is typically already ready, eliminating the gap between phrases. */
  Companion.prototype._playFromBufferPromise = function (bufferPromise, onDone) {
    var self = this;
    var turnId = (self._speakTurnId = (self._speakTurnId || 0) + 1);

    /* ctx.resume() est déjà fait dans _unlockAudio sur chaque user gesture,
       inutile de le rappeler à chaque phrase (peut introduire des micro-glitches). */

    bufferPromise.then(function (audioBuffer) {
      if (!audioBuffer) { onDone && onDone(); return; } /* fetch/decode failed earlier */
      if (turnId !== self._speakTurnId) { onDone && onDone(); return; }
      if (!self._audioCtx) { onDone && onDone(); return; }

      var ctx = self._audioCtx;
      var source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      var gain = ctx.createGain();
      gain.gain.value = 0.95;
      var analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyser.connect(gain);
      gain.connect(ctx.destination);

      self._currentSource = source;
      self._currentGain = gain;
      self._currentAnalyser = analyser;

      var freqBuf = new Uint8Array(analyser.frequencyBinCount);
      function loop() {
        analyser.getByteFrequencyData(freqBuf);
        var sum = 0;
        for (var i = 0; i < freqBuf.length; i++) sum += freqBuf[i];
        var amp = (sum / freqBuf.length) / 128;
        if (self.orb) self.orb.setAudioAmp(Math.min(1, amp));
        self._currentAudioRaf = requestAnimationFrame(loop);
      }
      loop();

      var doneCalled = false;
      function done() {
        if (doneCalled) return;
        doneCalled = true;
        self._releaseCurrentAudioRefs();
        onDone && onDone();
      }
      source.onended = done;
      /* Defensive resume: iOS Safari can suspend the AudioContext between phrases
         (system audio focus changes, notification interrupts, etc.). Cheap if already running. */
      if (ctx.state === 'suspended') {
        try { ctx.resume(); } catch (e) {}
      }
      try {
        /* 20ms lookahead : laisse le graph se câbler proprement, supprime le clic
           transitoire au start, et garantit l'enchaînement avec la phrase précédente */
        source.start(ctx.currentTime + 0.02);
      } catch (err) {
        console.warn('[Pulse] source.start() failed :', err);
        done();
      }
    }).catch(function (err) {
      console.warn('[Pulse] play promise rejected :', err);
      onDone && onDone();
    });
  };

  /* Visual-only fake speak (no real TTS) — used when voice fails or disabled */
  Companion.prototype.fakeSpeak = function (text) {
    var self = this;
    var speakingFor = Math.min(4500, 1500 + text.length * 12);
    var t0 = performance.now();
    function tick() {
      var dt = performance.now() - t0;
      if (dt > speakingFor) {
        if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
        self.setStatus('Touchez pour parler ou tapez votre question');
        return;
      }
      if (self.orb) self.orb.setAudioAmp(0.3 + Math.random() * 0.5);
      setTimeout(tick, 90);
    }
    tick();
  };

  /* ── Mock fallback (no backend deployed yet) ────────────── */
  Companion.prototype.replyFromMock = function (text) {
    var self = this;
    var delay = 700 + Math.random() * 500;
    setTimeout(function () {
      var answer = pickAnswer(text);
      if (self.orb) self.orb.setState('speaking');
      self.setStatus('Pulse parle…');
      self.addMsg('bot', md(answer));
      self.history.push({ role: 'assistant', content: answer });
      var speakingFor = Math.min(4500, 1500 + answer.length * 12);
      var t0 = performance.now();
      function fakeSpeak() {
        var dt = performance.now() - t0;
        if (dt > speakingFor) {
          if (self.orb) { self.orb.setAudioAmp(0); self.orb.setState('idle'); }
          self.setStatus('Touchez pour parler ou tapez votre question');
          return;
        }
        if (self.orb) self.orb.setAudioAmp(0.3 + Math.random() * 0.5);
        setTimeout(fakeSpeak, 90);
      }
      fakeSpeak();
    }, delay);
  };

  /* Transcribe a recorded audio Blob via Whisper, then send the result as user msg.
     Whisper handles proper nouns (ALTO, Louis-Gabriel) better than Web Speech API. */
  Companion.prototype._transcribeAndSend = function (blob, fallbackText) {
    var self = this;
    if (!blob || blob.size < 1000) {
      /* Audio too short — fall back to Web Speech interim if available */
      if (fallbackText && fallbackText.trim()) self.send(fallbackText.trim());
      return;
    }
    if (!USE_BACKEND) {
      if (fallbackText && fallbackText.trim()) self.send(fallbackText.trim());
      return;
    }
    self.setStatus('Transcription en cours…');
    var fd = new FormData();
    fd.append('audio', blob, 'audio.webm');
    fd.append('language', 'fr');
    fetch(PULSE_API_URL + '/api/transcribe', { method: 'POST', body: fd })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var text = data && data.text && data.text.trim();
        if (text) {
          self.send(text);
        } else if (fallbackText && fallbackText.trim()) {
          self.send(fallbackText.trim());
        } else {
          self.setStatus('Touchez pour parler ou tapez votre question');
        }
      })
      .catch(function (err) {
        console.warn('[Pulse] Whisper failed, falling back to Web Speech :', err);
        if (fallbackText && fallbackText.trim()) {
          self.send(fallbackText.trim());
        } else {
          self.setStatus('Touchez pour parler ou tapez votre question');
        }
      });
  };

  Companion.prototype.toggleMic = function () {
    var self = this;
    if (this.micActive) {
      this.micActive = false;
      this.micBtn.classList.remove('is-listening');
      /* Stop the MediaRecorder BEFORE closing the mic stream so the final chunk
         is captured. The recorder's onstop will fire and trigger _transcribeAndSend. */
      if (this._recorder && this._recorder.state !== 'inactive') {
        try { this._recorder.stop(); } catch (e) {}
      }
      this.mic.stop();
      if (this.orb) this.orb.setState('idle');
      /* If we have a recorder, status will be updated by it. Otherwise reset now. */
      if (!this._recorder) this.setStatus('Touchez pour parler ou tapez votre question');
      if (this._silenceTimer) { clearTimeout(this._silenceTimer); this._silenceTimer = null; }
      if (this.speech) try { this.speech.stop(); } catch (e) {}
      return;
    }
    this.micActive = true;
    this.micBtn.classList.add('is-listening');
    if (this.orb) this.orb.setState('listening');
    this.setStatus('J\'écoute…');

    /* Start MicMeter (handles getUserMedia + amplitude). On success, attach a
       MediaRecorder to its stream so we can capture audio in parallel for Whisper. */
    Promise.resolve(this.mic.start()).then(function () {
      if (!self.micActive) return; /* user toggled off before stream ready */
      if (window.MediaRecorder && self.mic && self.mic.stream) {
        self._recordedChunks = [];
        var options = {};
        /* Prefer opus in webm for best Whisper compatibility */
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          options.mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm')) {
          options.mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/mp4')) {
          options.mimeType = 'audio/mp4'; /* iOS Safari fallback */
        }
        try {
          self._recorder = new MediaRecorder(self.mic.stream, options);
        } catch (e) {
          try { self._recorder = new MediaRecorder(self.mic.stream); } catch (e2) { self._recorder = null; }
        }
        if (self._recorder) {
          self._recorder.ondataavailable = function (ev) {
            if (ev.data && ev.data.size > 0) self._recordedChunks.push(ev.data);
          };
          self._recorder.onstop = function () {
            var mime = (self._recorder && self._recorder.mimeType) || 'audio/webm';
            var blob = new Blob(self._recordedChunks, { type: mime });
            self._recordedChunks = [];
            var fallback = self._currentInterim;
            self._recorder = null;
            self._transcribeAndSend(blob, fallback);
          };
          try { self._recorder.start(); } catch (e) { self._recorder = null; }
        }
      }
    }).catch(function (err) {
      console.warn('[Pulse] mic permission denied :', err);
      self.micActive = false;
      self.micBtn.classList.remove('is-listening');
      if (self.orb) self.orb.setState('idle');
      self.setStatus('Autorisation micro refusée — vérifiez les permissions du navigateur');
      if (self.speech) try { self.speech.stop(); } catch (e) {}
    });

    if (this.speech) {
      this._currentInterim = '';
      var SILENCE_MS = 1500;

      this.speech.onresult = function (e) {
        var s = '';
        for (var i = 0; i < e.results.length; i++) s += e.results[i][0].transcript;
        self._currentInterim = s;
        self.input.value = s;
        self.sendBtn.classList.toggle('is-visible', s.trim().length > 0);
        if (s.trim().length > 0) {
          if (self._silenceTimer) clearTimeout(self._silenceTimer);
          self._silenceTimer = setTimeout(function () {
            self._silenceTimer = null;
            if (!self.micActive) return;
            if (self._currentInterim && self._currentInterim.trim()) {
              try { self.speech.stop(); } catch (e) {}
            }
          }, SILENCE_MS);
        }
      };
      this.speech.onend = function () {
        if (self._silenceTimer) { clearTimeout(self._silenceTimer); self._silenceTimer = null; }
        if (!self.micActive) return;
        /* If recorder is active, we don't send from here — recorder.onstop handles it
           via Whisper. Just toggle mic off, which stops the recorder. */
        if (self._recorder && self._recorder.state !== 'inactive') {
          self.toggleMic(); /* triggers recorder.stop() → onstop → Whisper → send */
        } else {
          /* No recorder (unsupported browser) — fall back to Web Speech interim */
          var toSend = self._currentInterim;
          self.toggleMic();
          if (toSend && toSend.trim()) {
            self.send(toSend.trim());
            self.input.value = '';
          }
        }
      };
      this.speech.onerror = function (e) {
        if (self._silenceTimer) { clearTimeout(self._silenceTimer); self._silenceTimer = null; }
        if (e && e.error === 'not-allowed') {
          self.setStatus('Autorisation micro refusée — vérifiez les permissions du navigateur');
        }
        if (self.micActive) self.toggleMic();
      };
      var startSpeech = function () {
        try { self.speech.start(); }
        catch (err) {
          if (err && err.name === 'InvalidStateError') {
            setTimeout(function () { try { self.speech.start(); } catch (e) {} }, 120);
          }
        }
      };
      startSpeech();
    }
  };

  /* ── Boot ───────────────────────────────────────────────── */
  function boot() {
    var c = new Companion();
    /* Expose API globale pour que d'autres sections (protocoles, pulse-intro, etc.)
       puissent ouvrir Pulse + envoyer une question programmatiquement. */
    window.altoPulseAsk = function (question) {
      if (!c || !question) return;
      try {
        c._unlockAudio && c._unlockAudio();
        if (!c.opened) c.open();
        setTimeout(function () { c.send(question); }, c.opened ? 50 : 650);
      } catch (e) { console.warn('[Pulse] altoPulseAsk failed :', e); }
    };
    /* Ouvre Pulse sans envoyer de message — pour les CTA type "Essayer Pulse". */
    window.altoPulseOpen = function () {
      if (!c) return;
      try {
        c._unlockAudio && c._unlockAudio();
        if (!c.opened) c.open();
      } catch (e) { console.warn('[Pulse] altoPulseOpen failed :', e); }
    };
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
